<template>
  <div class="cookie-consent-overlay">
    <div class="card bg-transparent border-0">
      <div class="card-body text-start bg-transparent text-light">
        <template v-if="isMobile">
          <div class="f-help">
            {{ $t('cookie_consent.message_mobile') }}
            <span
              v-if="showPrivacyPolicyLink"
              v-html="$t('cookie_consent.cta_to_modal_mobile')"
            ></span>
          </div>
        </template>

        <template v-else>
          <div class="f-help">
            {{ $t('cookie_consent.message') }}
          </div>
          <div
            v-if="showPrivacyPolicyLink"
            class="f-help"
            v-html="$t('cookie_consent.cta_to_modal')"
          ></div>
        </template>
        <div>
          <div class="d-flex justify-content-end">
            <button
              class="btn btn-dark btn-gray-dark text-light me-2"
              :class="{ disabled: loading }"
              :disabled="loading"
              type="button"
              @click.prevent="storeCookieValue(false)"
            >
              {{ $t('cookie_consent.decline') }}
            </button>
            <button
              class="btn btn-light fw-bold"
              :class="{ disabled: loading }"
              :disabled="loading"
              type="button"
              @click.prevent="storeCookieValue(true)"
            >
              {{ $t('cookie_consent.accept') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import TitleSection from '~concierge/components/TitleSection.vue'
import CustomCheckbox from '~conciergeUiCommons/components/CustomCheckbox.vue'
import AsyncImage from '~conciergeUiCommons/components/AsyncImage.vue'
import { defineComponent, ref, onMounted, onUpdated } from 'vue'
import { usePrivacyPolicyModalStore } from '~conciergeUiCommons/store/privacyPolicyModalStore'
import axios, { AxiosError } from 'axios'
import { useMobileDetection } from '~conciergeUiCommons/composables/deviceDetection'

export default defineComponent({
  name: 'ConsentQuestion',
  components: { CustomCheckbox, AsyncImage, TitleSection },
  props: {
    showPrivacyPolicyLink: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    const privacyPolicyModalStore = usePrivacyPolicyModalStore()
    const isEvent = privacyPolicyModalStore.service === 'conciergeEvent'
    const isMobile = useMobileDetection().isMobile

    const handlePrivacyPolicyClick = () => {
      privacyPolicyModalStore.openModal()
    }

    const bindPrivacyPolicyClick = () => {
      if (!props.showPrivacyPolicyLink) {
        return
      }

      const privacyPolicyElement = document.getElementById('privacyPolicy')

      if (privacyPolicyElement) {
        privacyPolicyElement.style.cursor = 'pointer'
        privacyPolicyElement.removeEventListener('click', handlePrivacyPolicyClick)
        privacyPolicyElement.addEventListener('click', handlePrivacyPolicyClick)
      }
    }

    onMounted(bindPrivacyPolicyClick)
    onUpdated(bindPrivacyPolicyClick)

    return {
      privacyPolicyModalStore,
      isEvent,
      isMobile
    }
  },
  emits: ['cookie-compliance'],
  data() {
    return {
      editingOther: false,
      hasImages: false,
      consent: ref(false),
      loading: ref(false)
    }
  },

  methods: {
    async storeCookieValue(value: boolean): Promise<void> {
      this.consent = value
      this.loading = true
      let routeString = 'conciergeForm.CookieConsent'
      if (this.isEvent) {
        routeString = this.$page.props.baseRoute + 'CookieConsent'
      } else if (this.$page.props.routePrefix) {
        routeString = this.$page.props.routePrefix + 'CookieConsent'
      }

      try {
        await axios.patch(route(routeString), {
          consentCookieCompliance: this.consent
        })
        this.$emit('cookie-compliance', true)
        this.loading = false
      } catch (error: unknown | Error | AxiosError) {
        this.loading = false
        throw error
      }
    }
  }
})
</script>

<style lang="scss" scoped>
:deep(em#privacyPolicy) {
  text-decoration: underline;
  transition: all 0.3s ease-in-out;

  &:hover {
    text-decoration: none;
  }
}

.cookie-consent-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  padding: 1rem;
  z-index: 1000;

  .btn {
    font-size: var(--font-size-smaller);
  }
}
</style>
